import React, { FC, useEffect, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@material-ui/core';
import { createUseStyles } from 'src/theming';
import { CollaborativeSource, CreateTaskOptions } from 'src/interfaces/api/Collaborative';
import { Controller, useForm } from 'react-hook-form';
import { CriticitySelect, PrioritySelect } from 'src/components/ApiField';
import DateTimePicker from 'src/components/DateTimePicker';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';
import EmployeeSelect from 'src/components/ApiField/EmployeeSelect';
import moment from 'moment';

const useStyles = createUseStyles({
  title: {
    width: 400,
    '& h2': {
      color: 'rgba(0, 0, 0, 0.87)',
    },
  },
  error: {
    color: '#f44336',
    fontSize: '.75rem',
    padding: '0 25px',
  },
  formControl: {
    marginBottom: '16px !important',
  },
});

const defaultFormValues = {
  title: '',
  text: '',
  deadline: moment().format('YYYY-MM-DD HH:mm:ss'),
  criticity: undefined,
  priority: undefined,
  employeesIds: [],
};

interface Props {
  taskSource?: CollaborativeSource;
  open: boolean;
  onClose: () => void;
  onValidate: (createTaskOptions: CreateTaskOptions) => void;
}

const AddTaskDialog: FC<Props> = ({ open, taskSource, onClose, onValidate }) => {
  const classes = useStyles();

  const [formError, setFormError] = useState<string | undefined>();
  const {
    reset,
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<CreateTaskOptions>({
    defaultValues: {
      ...defaultFormValues,
      attachedTo: taskSource,
    },
  });

  useEffect(() => {
    setFormError(undefined);
  }, []);

  const onSubmit = async (createTaskOptions: CreateTaskOptions) => {
    try {
      setFormError(undefined);
      await onValidate(createTaskOptions);
      reset(defaultFormValues);
      onClose();
    } catch (e) {
      setFormError(e.message);
    }
  };

  return (
    <Dialog onClose={onClose} scroll='paper' open={open}>
      <DialogTitle className={classes.title}>Créer une tâche</DialogTitle>

      <DialogContent>
        <MuiPickersUtilsProvider utils={MomentUtils}>
          <Controller
            name='title'
            control={control}
            rules={{ required: { value: true, message: 'Le champ est requis' } }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                className={classes.formControl}
                label='Titre'
                placeholder='Retrouver la tâche plus tard'
                variant='outlined'
                fullWidth
                error={undefined !== error}
                helperText={error?.message}
              />
            )}
          />

          <Controller
            name='text'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                className={classes.formControl}
                label='Texte'
                placeholder='Description de la tâche (facultatif)'
                variant='outlined'
                multiline
                rows={3}
                fullWidth
                error={undefined !== error}
                helperText={error?.message}
              />
            )}
          />

          <Controller
            name='criticity'
            control={control}
            rules={{ required: { value: true, message: 'Le champ est requis' } }}
            render={({ field, fieldState: { error } }) => (
              <CriticitySelect
                {...field}
                className={classes.formControl}
                fullWidth
                error={undefined !== error}
                helperText={error?.message}
              />
            )}
          />

          <Controller
            name='priority'
            control={control}
            rules={{ required: { value: true, message: 'Le champ est requis' } }}
            render={({ field, fieldState: { error } }) => (
              <PrioritySelect
                {...field}
                className={classes.formControl}
                fullWidth
                error={undefined !== error}
                helperText={error?.message}
              />
            )}
          />

          <Controller
            name='deadline'
            control={control}
            rules={{ required: { value: true, message: 'Le champ est requis' } }}
            render={({ field: { onChange, ...fieldOthers }, fieldState: { error } }) => (
              <DateTimePicker
                {...fieldOthers}
                onChange={(date) => onChange(date ? date.format('YYYY-MM-DD HH:mm:ss') : undefined)}
                className={classes.formControl}
                label='Date limite'
                format='DD/MM/YYYY HH:mm'
                inputVariant='outlined'
                helperText={error?.message}
                error={undefined !== error}
                disablePast
                fullWidth
                ampm={false}
              />
            )}
          />

          <Controller
            name='employeesIds'
            control={control}
            rules={{ required: { value: true, message: 'Le champ est requis' } }}
            render={({ field, fieldState: { error } }) => (
              <EmployeeSelect
                {...field}
                label='Employés associés'
                className={classes.formControl}
                multiple
                error={undefined !== error}
                helperText={error?.message}
                fullWidth
              />
            )}
          />
        </MuiPickersUtilsProvider>
      </DialogContent>

      <DialogActions>
        <Button type='button' onClick={onClose} color='primary'>
          Annuler
        </Button>

        <Button type='submit' color='primary' disabled={isSubmitting} onClick={handleSubmit(onSubmit)}>
          Créer
        </Button>
      </DialogActions>

      {formError && <p className={classes.error}>{formError}</p>}
    </Dialog>
  );
};

export default AddTaskDialog;
